import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, MonoTypeOperatorFunction, Observable } from 'rxjs';

import { finalize, multicast, refCount } from 'rxjs/operators';
import { Destroyable$ } from 'webcommon/shared/ap-base-class-ng1520';


// This is a utility class to track loading by categories,
// and allow you to easily tell whether anything is loading or not
@Injectable()
export class LoadTracker<TCategory extends string | number> implements OnDestroy {
  private totalOutstandingLoads = 0;
  private loadCountByCategory = new Map<TCategory, number>();
  private totalLoadingChange$ = new BehaviorSubject<boolean>(false);

  // This should be safe for use with default change detection and avoiding ExpressionChangedAfterItHasBeenCheckedError
  // because it uses async EventEmitter.
  // This can even be used directly in an @Output.
  // Another issue to consider is async pipe subscribing to this Observable after initial emissions already happened,
  // which means it is unlikely to have the expected behavior.
  // This Observable is piping directly off the BehaviorSubject to try to handle that.
  readonly safeLoadingChange$: Observable<boolean> = this.totalLoadingChange$.pipe(
    multicast(() => new EventEmitter<boolean>(true)),
    refCount(),
  );


  constructor(
    @Inject('LoadTracker_loadCategories') loadCategories: TCategory[],
    @Inject('LoadTracker_destroyable') private destroyable?: Destroyable$
  ) {
    this.destroyable?.onDestroy$.subscribe(() => {
      this.ngOnDestroy();
    });

    loadCategories.forEach((category) => {
      this.loadCountByCategory.set(category, 0);
    });
  }

  // this function is for help with type inference
  static create<T extends string | number>(
    destroyable: Destroyable$ | undefined,
    ...categories: T[]
  ): LoadTracker<T> {
    return new LoadTracker(categories, destroyable);
  }

  ngOnDestroy(): void {
    this.totalLoadingChange$.complete();
  }

  // This is a helper method to hook EventEmitters up to this tracker.
  // Make sure this tracker gets properly destroyed via ngOnDestroy or the constructor parameter though,
  // or else this could cause a memory leak.
  // Also, many times an async new EventEmitter(true) may be necessary to avoid ExpressionChangedAfterItHasBeenCheckedError
  emitTo(eventEmitter: EventEmitter<boolean>): this {
    this.totalLoadingChange$.subscribe((val) => {
      eventEmitter.emit(val);
    });
    return this;
  }

  // unlike safeLoadingChange, this is not guaranteed to be safe for change detection
  // see the spec for examples
  isLoading(category?: TCategory): boolean {
    if (!category) {
      return this.totalOutstandingLoads > 0;
    }

    const count = this.getLoadCount(category);
    return count > 0;
  }

  start(category: TCategory): TCategory {
    const alreadyLoading = this.isLoading();

    this.loadCountByCategory.set(category, this.getLoadCount(category) + 1);
    ++this.totalOutstandingLoads;

    if (!alreadyLoading) {
      this.totalLoadingChange$.next(true);
    }
    return category;
  }

  stop(category: TCategory): void {
    const alreadyLoading = this.isLoading();

    const count = this.getLoadCount(category);
    if (count > 0) {
      // to keep these data structures in sync,
      // I should only subtract 1 from the total if 1 is also subtracted from the category.
      this.loadCountByCategory.set(category, count - 1);

      const totalOutstandingLoads = this.totalOutstandingLoads;
      if (totalOutstandingLoads > 0) {
        this.totalOutstandingLoads = totalOutstandingLoads - 1;
      }
    }

    if (alreadyLoading && !this.isLoading()) {
      this.totalLoadingChange$.next(false);
    }
  }

  finalizeStopLoad<T>(category: TCategory, callback?: () => void): MonoTypeOperatorFunction<T> {
    return finalize(() => {
      this.stop(category);
      if (callback) {
        callback();
      }
    });
  }

  // usage: for promises, instead of Observables
  async runAsync<TResult>(
    category: TCategory,
    fn: (loadId: TCategory) => Promise<TResult>,
  ): Promise<TResult> {
    const loadId = this.start(category);
    try {
      return await fn(loadId);
    } finally {
      this.stop(loadId);
    }
  }

  private getLoadCount(category: TCategory): number {
    const count = this.loadCountByCategory.get(category);
    if (!count && count !== 0) {
      throw new Error(`Invalid load category: ${category}`);
    }
    return count;
  }
}
